import React from 'react'
import styled, { css } from 'styled-components'
import Box from '@components/Box'
import { STypographyLarge, STypographyParagraph } from '@components/Typography'
import { ASSOCIATES } from '@components/Utils/functions'
import { above } from '@styles/mixins/breakpoints'

type SCardItem = {
  image: string
  title: string
  text?: string
}

type SCardProps = {
  isExtended?: boolean
  items: SCardItem[]
}

const Card = styled(Box)`
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  position: relative;
  background: ${({ theme }) => theme.colors.lightGray};
  max-width: none;
  ${above('md')(css`
    max-width: 216px;
  `)}
  ${above('xl')(css`
    max-width: none;
  `)}
`

const SNumber = styled(Box)`
  position: absolute;
  left: 20px;
  top: 20px;
  width: 32px;
  height: 32px;
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${({ theme }) => theme.colors.white};
  font-size: 14px;
  line-height: 20px;
  font-weight: 600;
  background-color: ${({ theme }) => theme.colors.green};
  border-radius: 50%;
  ${above('xl')(css`
    left: 24px;
    top: 24px;
  `)}
`

const SImage = styled.img<{ isExtended: boolean }>`
  display: block;
  object-fit: contain;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  max-width: ${({ isExtended }) => (isExtended ? '302px' : '330px')};
  position: absolute;
  right: 50%;
  bottom: 0;
  transform: translateX(50%);
  ${({ isExtended }) =>
    above('md')(css`
      max-width: ${isExtended ? '267px' : '250px'};
    `)}
  ${above('xl')(css`
    max-width: 380px;
  `)}
`
const SImageWrapper = styled(Box)<{ isExtended: boolean }>`
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  min-height: ${({ isExtended }) => (isExtended ? '176px' : '152px')};
  background: ${({ theme }) => theme.colors.steelGray};
  position: relative;
  display: flex;
  overflow: hidden;
  align-items: flex-end;
  ${above('xl')(css`
    min-height: none;
  `)}
`

const SCards: React.FC<SCardProps> = ({ items, isExtended = false }) => (
  <Box display="flex" flexWrap={{ xs: 'wrap', md: 'nowrap' }}>
    {items?.map((item, idx) => (
      <Card
        key={idx}
        width={{ xs: '100%', md: '33%' }}
        mr={idx !== items.length - 1 ? { xs: 0, md: '20px' } : 0}
        mb={idx !== items.length - 1 ? { xs: '20px', md: 0 } : 0}
      >
        <SImageWrapper isExtended={isExtended}>
          <SImage src={item.image} alt="Image" isExtended={isExtended} />
        </SImageWrapper>
        <SNumber>0{idx + 1}</SNumber>
        <Box
          padding={{
            xs: isExtended ? '12px 20px 20px 20px' : '20px 20px 24px 20px',
            md: '20px 20px 24px 20px',
            xl: '20px 24px 24px 24px',
          }}
        >
          <STypographyLarge fontWeight="600" dangerouslySetInnerHTML={{ __html: item.title }} />
          {item.text && (
            <STypographyLarge
              mt={{ xs: '4px', md: '8px', xl: '6px' }}
              dangerouslySetInnerHTML={{ __html: item.text }}
            />
          )}
        </Box>
      </Card>
    ))}
  </Box>
)

export default SCards
